import { useState, useEffect, useRef } from 'react'
import {
  useTask,
  useUpdateTask,
  useDeleteTask,
  useUsers,
  useAddTaskRelation,
  useRemoveTaskRelation,
  useSetTaskPrimaryContact,
  useContacts,
  useLeads,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDebounce } from '@/hooks/use-debounce'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useToast } from '@/hooks/use-toast'
import { formatDate } from '@/lib/utils'
import { Link } from 'react-router-dom'
import { Circle, CheckCircle2, Clock, Save, X, Plus, Trash2, Star } from 'lucide-react'
import type { Task } from '@/types'
import { CreateTaskModal } from '@/components/CreateTaskModal'
import { getTaskTypeIcon } from '@/lib/task-type'
import { explainError } from '@/lib/explain-error'

interface TaskDetailModalProps {
  taskId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const getStatusVariant = (status?: string): 'default' | 'secondary' | 'success' | 'warning' => {
  if (!status) return 'default'

  switch (status.toLowerCase()) {
    case 'completed':
      return 'success'
    case 'in progress':
      return 'warning'
    case 'not started':
      return 'default'
    default:
      return 'secondary'
  }
}

const getStatusIcon = (status?: string) => {
  if (!status) return <Circle className="h-4 w-4" />

  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4" />
    case 'in progress':
      return <Clock className="h-4 w-4" />
    default:
      return <Circle className="h-4 w-4" />
  }
}

export function TaskDetailModal({ taskId, open, onOpenChange }: TaskDetailModalProps) {
  const { data, isLoading, error } = useTask(taskId || '')
  const updateMutation = useUpdateTask()
  const deleteMutation = useDeleteTask()
  const { data: usersData } = useUsers({ limit: 10000 })
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const { toast } = useToast()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const TASK_STATUS = usePicklistOptions('task', 'status')
  const TASK_PRIORITY = usePicklistOptions('task', 'priority')
  const TASK_TYPE = usePicklistOptions('task', 'type')
  const CALL_TYPES = usePicklistOptions('task', 'call_type')
  const CALL_DISPOSITIONS = usePicklistOptions('task', 'call_disposition')

  const addRelationMutation = useAddTaskRelation()
  const removeRelationMutation = useRemoveTaskRelation()
  const setPrimaryMutation = useSetTaskPrimaryContact()

  const [formData, setFormData] = useState<Partial<Task>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Notify the new owner by email on owner change (default on; backend only emails
  // when owner_id actually changes). Mirrors the other detail pages.
  const [notifyOwner, setNotifyOwner] = useState(true)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)
  // Snapshot of the just-completed task so the follow-up modal survives the
  // parent clearing taskId on close (e.g. ActivityTimeline on detail pages).
  const [followUpTask, setFollowUpTask] = useState<typeof data | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [showContactSearch, setShowContactSearch] = useState(false)
  const debouncedContactSearch = useDebounce(contactSearch, 300)
  const contactSearchRef = useRef<HTMLInputElement>(null)
  // Company-only leads have no person name; fall back to company/email so they
  // aren't a blank, unidentifiable row in the search results.
  const leadLabel = (l: {
    first_name?: string
    last_name?: string
    company?: string
    email?: string
  }) =>
    `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.company || l.email || 'Unnamed lead'

  const task = data
  // Delete is allowed for the task owner or an admin, and never while impersonating
  // (read-only) — mirrors the Opportunities delete gate. RFP-sourced tasks are managed
  // in the RFP module, so they're not deletable here.
  const canDelete =
    !!task &&
    task.task_source !== 'rfp_task' &&
    (isAdmin || currentUser?.id === task.owner_id) &&
    !impersonatedUser
  // Filter contacts by account when task is related to an account
  const taskAccountId = task?.what_id?.startsWith('001') ? task.what_id : undefined
  const { data: contactsSearchData } = useContacts({
    search: debouncedContactSearch || undefined,
    limit: 5,
    sort_by: 'last_name',
    sort_order: 'asc',
    account_id: taskAccountId,
  } as any)
  const { data: leadsSearchData } = useLeads({
    search: debouncedContactSearch || undefined,
    limit: 5,
    sort_by: 'last_name',
    sort_order: 'asc',
  })
  const users = usersData?.data ?? []

  // Reset form when task data changes or modal opens/closes
  useEffect(() => {
    if (task) {
      setFormData({
        subject: task.subject,
        status: task.status,
        priority: task.priority,
        activity_date: task.activity_date || task.due_date,
        owner_id: task.owner_id,
        description: task.description || '',
        type: task.type || 'Task',
        call_type: task.call_type || '',
        call_disposition: task.call_disposition || '',
      })
    }
    setErrors({})
  }, [task, open])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.subject?.trim()) {
      newErrors.subject = 'Subject is required'
    }

    if (!formData.status) {
      newErrors.status = 'Status is required'
    }

    if (!formData.priority) {
      newErrors.priority = 'Priority is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    if (!taskId) return

    try {
      await updateMutation.mutateAsync({
        id: taskId,
        data: { ...formData, notify_owner: notifyOwner },
      })

      toast({
        title: 'Success',
        description: 'Task updated successfully.',
      })

      // Prompt follow-up task if status changed to Completed.
      // Snapshot the task BEFORE closing — parents (e.g. ActivityTimeline on
      // detail pages) null the taskId on close, which clears the live `task`
      // query and would otherwise leave the follow-up modal with no data.
      const shouldFollowUp = formData.status === 'Completed' && task?.status !== 'Completed'
      if (shouldFollowUp) {
        setFollowUpTask(task)
      }

      onOpenChange(false)

      if (shouldFollowUp) {
        setFollowUpModalOpen(true)
      }
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.error || explainError(error, 'Failed to update task')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    if (task) {
      setFormData({
        subject: task.subject,
        status: task.status,
        priority: task.priority,
        activity_date: task.activity_date || task.due_date,
        owner_id: task.owner_id,
        description: task.description || '',
        type: task.type || 'Task',
        call_type: task.call_type || '',
        call_disposition: task.call_disposition || '',
      })
    }
    setErrors({})
    onOpenChange(false)
  }

  const handleDelete = async () => {
    if (!taskId) return
    try {
      await deleteMutation.mutateAsync(taskId)
      toast({ title: 'Task deleted', description: 'The task has been deleted.' })
      setDeleteDialogOpen(false)
      onOpenChange(false)
    } catch (error) {
      const errorMessage =
        (error as any)?.response?.data?.error || explainError(error, 'Failed to delete task')
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-y-auto"
          aria-describedby="task-description"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {task && getTaskTypeIcon(task.type)}
              <span>{task?.subject || 'Task Details'}</span>
              {task?.status && (
                <Badge variant={getStatusVariant(task.status)} className="flex items-center gap-1">
                  {getStatusIcon(task.status)}
                  {task.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div id="task-description" className="space-y-6">
            {isLoading && (
              <div className="text-center py-8 text-muted-foreground">Loading task details...</div>
            )}

            {error && (
              <div className="text-center py-8 text-destructive">
                Error loading task: {error.message}
              </div>
            )}

            {task && (
              <>
                {/* Basic Information */}
                <section>
                  <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subject" className="required">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        value={formData.subject || ''}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        aria-required="true"
                        aria-invalid={!!errors.subject}
                        aria-describedby={errors.subject ? 'subject-error' : undefined}
                        className="mt-1"
                      />
                      {errors.subject && (
                        <p
                          id="subject-error"
                          className="text-sm text-destructive mt-1"
                          role="alert"
                        >
                          {errors.subject}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="status" className="required">
                        Status *
                      </Label>
                      <Select
                        value={formData.status || ''}
                        onValueChange={(value) => handleChange('status', value)}
                      >
                        <SelectTrigger
                          id="status"
                          className="mt-1"
                          aria-required="true"
                          aria-invalid={!!errors.status}
                          aria-describedby={errors.status ? 'status-error' : undefined}
                        >
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_STATUS.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.status && (
                        <p id="status-error" className="text-sm text-destructive mt-1" role="alert">
                          {errors.status}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="priority" className="required">
                        Priority *
                      </Label>
                      <Select
                        value={formData.priority || ''}
                        onValueChange={(value) => handleChange('priority', value)}
                      >
                        <SelectTrigger
                          id="priority"
                          className="mt-1"
                          aria-required="true"
                          aria-invalid={!!errors.priority}
                          aria-describedby={errors.priority ? 'priority-error' : undefined}
                        >
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          {TASK_PRIORITY.map((priority) => (
                            <SelectItem key={priority} value={priority}>
                              {priority}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.priority && (
                        <p
                          id="priority-error"
                          className="text-sm text-destructive mt-1"
                          role="alert"
                        >
                          {errors.priority}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="type">Type</Label>
                      <Select
                        value={formData.type || 'Task'}
                        onValueChange={(value) => {
                          handleChange('type', value)
                          // Call-only fields don't apply to other types — clear them
                          // so we never persist a stale direction/disposition.
                          if (value !== 'Call') {
                            handleChange('call_type', '')
                            handleChange('call_disposition', '')
                          }
                        }}
                      >
                        <SelectTrigger id="type" className="mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {(TASK_TYPE.length > 0
                            ? TASK_TYPE
                            : ['Task', 'Call', 'Email', 'Meeting', 'Other']
                          ).map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="owner_id">Task Owner</Label>
                      <Select
                        value={formData.owner_id || ''}
                        onValueChange={(value) => handleChange('owner_id', value)}
                      >
                        <SelectTrigger id="owner_id" className="mt-1">
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...users]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((user) => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          id="task-notify_owner"
                          checked={notifyOwner}
                          onCheckedChange={(checked) => setNotifyOwner(checked === true)}
                        />
                        <Label
                          htmlFor="task-notify_owner"
                          className="text-sm font-normal cursor-pointer"
                        >
                          Notify new owner by email (only if owner changes)
                        </Label>
                      </div>
                    </div>

                    {formData.type === 'Call' && (
                      <>
                        <div>
                          <Label htmlFor="call_type">Direction</Label>
                          <Select
                            value={formData.call_type || ''}
                            onValueChange={(value) => handleChange('call_type', value)}
                          >
                            <SelectTrigger id="call_type" className="mt-1">
                              <SelectValue placeholder="Select direction" />
                            </SelectTrigger>
                            <SelectContent>
                              {(CALL_TYPES.length > 0 ? CALL_TYPES : ['Inbound', 'Outbound']).map(
                                (ct) => (
                                  <SelectItem key={ct} value={ct}>
                                    {ct}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="call_disposition">Disposition</Label>
                          <Select
                            value={formData.call_disposition || ''}
                            onValueChange={(value) => handleChange('call_disposition', value)}
                          >
                            <SelectTrigger id="call_disposition" className="mt-1">
                              <SelectValue placeholder="Select disposition" />
                            </SelectTrigger>
                            <SelectContent>
                              {(CALL_DISPOSITIONS.length > 0
                                ? CALL_DISPOSITIONS
                                : [
                                    'Connected',
                                    'No Answer',
                                    'Left Voicemail',
                                    'Busy',
                                    'Wrong Number',
                                  ]
                              ).map((cd) => (
                                <SelectItem key={cd} value={cd}>
                                  {cd}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                </section>

                {/* Scheduling Information */}
                <section>
                  <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                    Scheduling
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="activity_date">Due Date</Label>
                      <DatePicker
                        id="activity_date"
                        value={formData.activity_date}
                        onChange={(v) => handleChange('activity_date', v || null)}
                        className="mt-1"
                        aria-label="Due Date"
                      />
                    </div>
                  </div>
                </section>

                {/* Description */}
                <section>
                  <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                    Description
                  </h3>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    placeholder="Enter task description..."
                    aria-label="Task description"
                  />
                </section>

                {/* Additional Information (Read-only) */}
                <section>
                  <h3 className="text-lg font-semibold mb-3 bg-muted px-3 py-1.5 rounded-sm">
                    Additional Information
                  </h3>
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <dt className="text-sm font-medium text-muted-foreground mb-1">
                        Contacts / Leads
                      </dt>
                      <dd className="space-y-2">
                        {/* Primary contact */}
                        {task.who_id && task.who_name && (
                          <div className="flex items-center gap-2">
                            <Link
                              to={
                                task.who_id.startsWith('00Q')
                                  ? `/leads/${task.who_id}`
                                  : `/contacts/${task.who_id}`
                              }
                              className="text-sm text-primary hover:underline"
                            >
                              {task.who_name}
                            </Link>
                            <Badge variant="outline" className="text-[10px]">
                              primary
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                try {
                                  await setPrimaryMutation.mutateAsync({
                                    taskId: taskId!,
                                    relationId: null,
                                  })
                                  toast({ title: 'Primary contact removed' })
                                } catch {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to remove primary contact',
                                    variant: 'destructive',
                                  })
                                }
                              }}
                              aria-label={`Remove primary contact ${task.who_name}`}
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </Button>
                          </div>
                        )}
                        {/* Additional contacts */}
                        {task.related_contacts?.map((rc) => (
                          <div key={rc.id} className="flex items-center gap-2">
                            <Link
                              to={
                                rc.relation_type === 'Lead'
                                  ? `/leads/${rc.relation_id}`
                                  : `/contacts/${rc.relation_id}`
                              }
                              className="text-sm text-primary hover:underline"
                            >
                              {rc.relation_name}
                            </Link>
                            <span className="text-xs text-muted-foreground">
                              ({rc.relation_type})
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-primary"
                              onClick={async () => {
                                try {
                                  await setPrimaryMutation.mutateAsync({
                                    taskId: taskId!,
                                    relationId: rc.relation_id,
                                  })
                                  toast({ title: `${rc.relation_name} set as primary contact` })
                                } catch {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to set primary contact',
                                    variant: 'destructive',
                                  })
                                }
                              }}
                              aria-label={`Set ${rc.relation_name} as primary contact`}
                            >
                              <Star className="h-3 w-3" aria-hidden="true" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                try {
                                  await removeRelationMutation.mutateAsync({
                                    taskId: taskId!,
                                    relationId: rc.relation_id,
                                  })
                                  toast({ title: 'Contact removed from task' })
                                } catch {
                                  toast({
                                    title: 'Error',
                                    description: 'Failed to remove contact',
                                    variant: 'destructive',
                                  })
                                }
                              }}
                              aria-label={`Remove ${rc.relation_name} from task`}
                            >
                              <X className="h-3 w-3" aria-hidden="true" />
                            </Button>
                          </div>
                        ))}
                        {!task.who_id &&
                          (!task.related_contacts || task.related_contacts.length === 0) && (
                            <span className="text-sm text-muted-foreground">None</span>
                          )}
                        {/* Add contact search */}
                        {showContactSearch ? (
                          <div className="relative">
                            <Input
                              ref={contactSearchRef}
                              value={contactSearch}
                              onChange={(e) => setContactSearch(e.target.value)}
                              placeholder="Search contacts or leads..."
                              className="h-8 text-sm"
                              aria-label="Search contacts or leads to add"
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setShowContactSearch(false)
                                  setContactSearch('')
                                }
                              }}
                            />
                            {debouncedContactSearch.length >= 2 && (
                              <div className="absolute z-10 mt-1 w-full bg-popover border rounded-md shadow-md max-h-40 overflow-y-auto">
                                {(contactsSearchData?.data ?? [])
                                  .filter(
                                    (c: any) =>
                                      c.id !== task.who_id &&
                                      !task.related_contacts?.some((rc) => rc.relation_id === c.id)
                                  )
                                  .slice(0, 3)
                                  .map((c: any) => (
                                    <button
                                      key={c.id}
                                      type="button"
                                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                      onClick={async () => {
                                        try {
                                          await addRelationMutation.mutateAsync({
                                            taskId: taskId!,
                                            relationId: c.id,
                                          })
                                          setContactSearch('')
                                          setShowContactSearch(false)
                                          toast({
                                            title: `${c.name || c.first_name + ' ' + c.last_name} added to task`,
                                          })
                                        } catch {
                                          toast({
                                            title: 'Error',
                                            description: 'Failed to add contact',
                                            variant: 'destructive',
                                          })
                                        }
                                      }}
                                    >
                                      {c.name || `${c.first_name} ${c.last_name}`}
                                      <span className="text-xs text-muted-foreground ml-1">
                                        (Contact)
                                      </span>
                                    </button>
                                  ))}
                                {(leadsSearchData?.data ?? [])
                                  .filter(
                                    (l: any) =>
                                      l.id !== task.who_id &&
                                      !task.related_contacts?.some((rc) => rc.relation_id === l.id)
                                  )
                                  .slice(0, 3)
                                  .map((l: any) => (
                                    <button
                                      key={l.id}
                                      type="button"
                                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                                      onClick={async () => {
                                        try {
                                          await addRelationMutation.mutateAsync({
                                            taskId: taskId!,
                                            relationId: l.id,
                                          })
                                          setContactSearch('')
                                          setShowContactSearch(false)
                                          toast({
                                            title: `${leadLabel(l)} added to task`,
                                          })
                                        } catch {
                                          toast({
                                            title: 'Error',
                                            description: 'Failed to add lead',
                                            variant: 'destructive',
                                          })
                                        }
                                      }}
                                    >
                                      {leadLabel(l)}
                                      <span className="text-xs text-muted-foreground ml-1">
                                        (Lead)
                                      </span>
                                    </button>
                                  ))}
                                {debouncedContactSearch.length >= 2 &&
                                  (contactsSearchData?.data ?? []).length === 0 &&
                                  (leadsSearchData?.data ?? []).length === 0 && (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">
                                      No results found
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              setShowContactSearch(true)
                              setTimeout(() => contactSearchRef.current?.focus(), 100)
                            }}
                            aria-label="Add contact or lead to task"
                          >
                            <Plus className="h-3 w-3 mr-1" aria-hidden="true" />
                            Add Contact
                          </Button>
                        )}
                      </dd>
                    </div>
                    {task.what_name && (
                      <div>
                        <dt className="text-sm font-medium text-muted-foreground">Related To</dt>
                        <dd className="mt-1">
                          {task.what_id ? (
                            <Link
                              to={
                                task.what_id.startsWith('006')
                                  ? `/opportunities/${task.what_id}`
                                  : `/accounts/${task.what_id}`
                              }
                              className="text-primary hover:underline"
                              aria-label={`View related: ${task.what_name}`}
                            >
                              {task.what_name}
                            </Link>
                          ) : (
                            task.what_name
                          )}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Created Date</dt>
                      <dd className="mt-1">
                        {task.created_at ? formatDate(task.created_at) : '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">Last Modified</dt>
                      <dd className="mt-1">
                        {task.updated_at ? formatDate(task.updated_at) : '-'}
                      </dd>
                    </div>
                  </dl>
                </section>

                {/* Action Buttons */}
                <div className="flex justify-between gap-3 pt-4 border-t">
                  <div>
                    {canDelete && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setDeleteDialogOpen(true)}
                        disabled={updateMutation.isPending || deleteMutation.isPending}
                        aria-label={`Delete task ${task.subject}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                        Delete
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={updateMutation.isPending}
                      aria-label="Cancel editing"
                    >
                      <X className="mr-2 h-4 w-4" aria-hidden="true" />
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSave}
                      disabled={updateMutation.isPending}
                      aria-label="Save task changes"
                    >
                      <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                      {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-task-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription id="delete-task-description">
              Are you sure you want to delete the task &ldquo;{task?.subject}&rdquo;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete task ${task?.subject}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {followUpTask && (
        <CreateTaskModal
          open={followUpModalOpen}
          onOpenChange={(open) => {
            setFollowUpModalOpen(open)
            if (!open) setFollowUpTask(null)
          }}
          defaultSubject={`Follow-up: ${followUpTask.subject}`}
          // Related To = the account/opportunity only (the contact is carried via
          // defaultContacts). No who fallback — otherwise the contact gets mis-shown as
          // "Related To" and the actual account is hidden. The task detail endpoint doesn't
          // return what_type, so derive it from the Salesforce ID prefix (001=Account,
          // 006=Opportunity), falling back to any what_type present (e.g. RFP).
          relatedToId={followUpTask.what_id}
          relatedToType={
            followUpTask.what_id?.startsWith('006')
              ? 'Opportunity'
              : followUpTask.what_id?.startsWith('001')
                ? 'Account'
                : followUpTask.what_id?.startsWith('500')
                  ? 'Ticket'
                  : followUpTask.what_type
          }
          relatedToName={followUpTask.what_name}
          ownerId={followUpTask.owner_id}
          defaultContacts={[
            ...(followUpTask.who_id && followUpTask.who_name
              ? [
                  {
                    id: followUpTask.who_id,
                    name: followUpTask.who_name,
                    type: (followUpTask.who_id.startsWith('00Q') ? 'Lead' : 'Contact') as
                      | 'Contact'
                      | 'Lead',
                  },
                ]
              : []),
            ...(followUpTask.related_contacts?.map((rc) => ({
              id: rc.relation_id,
              name: rc.relation_name,
              type: rc.relation_type as 'Contact' | 'Lead',
            })) || []),
          ]}
        />
      )}
    </>
  )
}
